import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { WORK_GROUPS } from "@/lib/data";

const FILTERS = [{ id: "all", label: "All Work" }, ...WORK_GROUPS.map((g) => ({ id: g.id, label: g.label }))];

export default function Work() {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const close = useCallback(() => setLightbox(null), []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  const totalCount = useMemo(
    () => WORK_GROUPS.reduce((n, g) => n + g.items.length, 0),
    []
  );

  const groups = filter === "all" ? WORK_GROUPS : WORK_GROUPS.filter((g) => g.id === filter);

  return (
    <div data-testid="work-page" className="bg-cream">
      {/* ===== Page heading ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 pt-20 sm:pt-22 md:pt-28 pb-10 sm:pb-14">
        <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-6 sm:gap-8">
          <div className="col-span-12 md:col-span-4">
            <p className="font-mono-label text-terracotta">Recent Work</p>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.98]">
              Projects.<br />
              One <em className="italic text-terracotta">studio</em>.
            </h1>
            <p className="mt-6 sm:mt-8 text-base sm:text-lg text-stone-text max-w-2xl leading-relaxed">
              A ledger of recent commissions across custom table tops, terrazzo floors and walls, and cement cladding brick installations — all cast, cured and installed by our own team.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Filter chips (sticky on scroll) ===== */}
      <section className="sticky top-[68px] sm:top-[76px] z-30 bg-cream/95 backdrop-blur border-y border-warm">
        <div className="px-4 sm:px-6 md:px-16 lg:px-20 max-w-[1600px] mx-auto">
          <div className="flex gap-2 sm:gap-3 overflow-x-auto no-scrollbar py-3 sm:py-4">
            {FILTERS.map((f) => {
              const isActive = filter === f.id;
              const count =
                f.id === "all"
                  ? totalCount
                  : WORK_GROUPS.find((g) => g.id === f.id)?.items.length ?? 0;
              return (
                <button
                  key={f.id}
                  data-testid={`work-filter-${f.id}`}
                  onClick={() => setFilter(f.id)}
                  className={`shrink-0 font-mono-label px-4 sm:px-5 py-2 sm:py-2.5 border transition-colors whitespace-nowrap ${
                    isActive
                      ? "bg-ink text-cream border-ink"
                      : "bg-cream text-ink border-warm hover:border-ink"
                  }`}
                >
                  {f.label} <span className="opacity-60 ml-1">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== Group sections ===== */}
      {groups.map((group, gIdx) => (
        <section
          key={group.id}
          id={group.id}
          data-testid={`work-group-${group.id}`}
          className={`scroll-mt-32 px-4 sm:px-6 md:px-16 lg:px-20 py-14 sm:py-20 md:py-28 ${
            gIdx % 2 === 0 ? "bg-cream" : "bg-stone"
          }`}
        >
          <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-12 gap-6 sm:gap-8 mb-10 sm:mb-14">
              <div className="col-span-12 md:col-span-4">
                <p className="font-mono-label text-terracotta">{`0${gIdx + 1}`}</p>
              </div>
              <div className="col-span-12 md:col-span-8">
                <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
                  {group.label} <em className="italic text-terracotta">·</em> <span className="text-stone-text text-2xl sm:text-3xl md:text-5xl">{group.items.length}</span>
                </h2>
                <p className="mt-4 sm:mt-5 text-sm sm:text-base text-stone-text max-w-2xl leading-relaxed">
                  {group.tagline}
                </p>
              </div>
            </div>

            {/* Masonry-ish grid — CSS columns give a true magazine feel */}
            <div
              data-testid={`work-grid-${group.id}`}
              className="columns-2 sm:columns-3 lg:columns-4 gap-3 sm:gap-4 md:gap-5 [column-fill:_balance]"
            >
              {group.items.map((item, i) => (
                <button
                  key={item.image}
                  data-testid={`work-tile-${group.id}-${i + 1}`}
                  onClick={() => setLightbox({ ...item, group: group.label })}
                  className="mb-3 sm:mb-4 md:mb-5 block w-full text-left group focus:outline-none focus:ring-2 focus:ring-terracotta focus:ring-offset-2 focus:ring-offset-cream break-inside-avoid"
                  aria-label={`View ${item.label}`}
                >
                  <div className="image-zoom-wrap relative overflow-hidden bg-warm/40">
                    <img
                      src={item.image}
                      alt={item.label}
                      loading="lazy"
                      className="w-full h-auto object-cover block"
                    />
                    <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/20 transition-colors" />
                    <div className="absolute bottom-0 left-0 right-0 p-2.5 sm:p-3 md:p-4 bg-gradient-to-t from-ink/85 via-ink/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="font-mono-label text-cream text-[0.55rem] sm:text-[0.65rem] md:text-xs">
                        {group.label}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* ===== CTA ===== */}
      <section className="px-4 sm:px-6 md:px-16 lg:px-20 py-12 sm:py-16 md:py-20 bg-cream-2">
        <div className="max-w-[1200px] mx-auto text-center">
          <p className="font-mono-label text-terracotta mb-3 sm:mb-4">— Have something in mind?</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-6xl leading-[1.05]">
            Send us a drawing.<br />We'll send a <em className="italic text-terracotta">sample</em>.
          </h2>
          <p className="mt-5 sm:mt-6 text-sm sm:text-base text-stone-text max-w-xl mx-auto leading-relaxed">
            Every piece above started with a photo, a sketch, or a colour reference. Yours can too.
          </p>
          <Link
            to="/request-quote"
            data-testid="work-cta"
            className="inline-flex mt-8 sm:mt-10 font-mono-label px-7 sm:px-8 py-3.5 sm:py-4 bg-ink text-cream hover:bg-terracotta transition-colors"
          >
            Request a Sample →
          </Link>
        </div>
      </section>

      {/* ===== Lightbox ===== */}
      {lightbox && (
        <div
          data-testid="work-lightbox"
          onClick={close}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur flex items-center justify-center p-4 sm:p-6 md:p-12 cursor-zoom-out"
        >
          <button
            data-testid="work-lightbox-close"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-10 md:right-10 font-mono-label text-cream hover:text-terracotta z-10 px-3 py-2 bg-ink/50 rounded"
          >
            Close ✕
          </button>
          <div className="max-w-[94vw] max-h-[86vh] flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.image}
              alt={lightbox.label}
              className="max-w-full max-h-[76vh] object-contain shadow-2xl"
            />
            <div className="text-center">
              <p className="font-mono-label text-cream/60">{lightbox.group}</p>
              <p className="font-serif-display text-cream text-xl sm:text-2xl md:text-3xl mt-1">{lightbox.label}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
